import { RECIPES, type SoundName } from './synth';

export interface SoundSettings {
  muted: boolean;
  /** 0..1 */
  volume: number;
}

export const SOUND_STORAGE_KEY = '20fight:sound';
const DEFAULTS: SoundSettings = { muted: false, volume: 0.7 };

function readSettings(): SoundSettings {
  try {
    const raw = globalThis.localStorage?.getItem(SOUND_STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<SoundSettings>;
    return {
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : DEFAULTS.muted,
      volume:
        typeof parsed.volume === 'number' && Number.isFinite(parsed.volume)
          ? clamp01(parsed.volume)
          : DEFAULTS.volume,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function clamp01(v: number) {
  return Math.min(1, Math.max(0, v));
}

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  const w = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Global sound engine. Lazily creates one AudioContext, unlocks it on the first user gesture,
 * persists mute/volume to localStorage and never throws (no-op where Web Audio is missing).
 */
export class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private settings: SoundSettings = readSettings();
  private listeners = new Set<() => void>();
  private unlockInstalled = false;
  private lastPlayed = new Map<SoundName, number>();

  /** Minimum gap per sound (ms) so bursts of events don't machine-gun the speakers. */
  private static readonly MIN_GAP: Partial<Record<SoundName, number>> = {
    reaction: 60,
    click: 30,
    tick: 200,
  };

  constructor() {
    this.installUnlock();
  }

  get muted() {
    return this.settings.muted;
  }

  get volume() {
    return this.settings.volume;
  }

  getSettings = (): SoundSettings => this.settings;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  setMuted(muted: boolean) {
    this.update({ muted });
  }

  toggleMuted() {
    this.update({ muted: !this.settings.muted });
  }

  setVolume(volume: number) {
    this.update({ volume: clamp01(volume) });
  }

  /** Play a sound now. Silently ignored when muted, locked or unsupported. */
  play(name: SoundName) {
    if (this.settings.muted || this.settings.volume <= 0) return;
    try {
      const ctx = this.ensureContext();
      if (!ctx || !this.master) return;
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
      const nowMs = performance.now();
      const gap = SoundEngine.MIN_GAP[name] ?? 0;
      if (gap && nowMs - (this.lastPlayed.get(name) ?? -Infinity) < gap) return;
      this.lastPlayed.set(name, nowMs);
      RECIPES[name](ctx, this.master, ctx.currentTime + 0.01);
    } catch {
      // Audio is decoration: never let it break the game.
    }
  }

  /** Resume the context (call from a user gesture). Safe to call repeatedly. */
  unlock() {
    try {
      const ctx = this.ensureContext();
      if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    } catch {
      /* ignore */
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = audioContextCtor();
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor();
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.ratio.value = 4;
      this.master = this.ctx.createGain();
      this.master.gain.value = this.settings.muted ? 0 : this.curve(this.settings.volume);
      this.master.connect(comp).connect(this.ctx.destination);
    } catch {
      this.ctx = null;
      this.master = null;
    }
    return this.ctx;
  }

  /** Perceptual volume curve. */
  private curve(v: number) {
    return v * v * 0.9;
  }

  private update(patch: Partial<SoundSettings>) {
    this.settings = { ...this.settings, ...patch };
    if (this.master && this.ctx) {
      try {
        // Muting also fades the master so a long fanfare already playing stops within ~60 ms
        // (a short ramp, not a hard cut, so there is no click).
        const target = this.settings.muted ? 0 : this.curve(this.settings.volume);
        this.master.gain.setTargetAtTime(target, this.ctx.currentTime, 0.02);
      } catch {
        /* ignore */
      }
    }
    try {
      globalThis.localStorage?.setItem(SOUND_STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      /* storage full / disabled */
    }
    this.listeners.forEach((l) => l());
  }

  private installUnlock() {
    if (this.unlockInstalled || typeof window === 'undefined' || !audioContextCtor()) return;
    this.unlockInstalled = true;
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    const handler = () => {
      this.unlock();
      if (this.ctx?.state === 'running')
        events.forEach((e) => window.removeEventListener(e, handler, true));
    };
    events.forEach((e) => window.addEventListener(e, handler, { capture: true, passive: true }));
  }
}

/** App-wide singleton. */
export const sound = new SoundEngine();
